import {
  useState, useCallback, useEffect, useRef,
} from 'react';
import _ from 'underscore';
import { uploadFile } from '@bingads-webui-react/file-upload-service';

import {
  IUseFileUploadProgressOptions,
  IFileObject,
  FilesUploadProgressInfo,
  FilesUploadCompletedInfo,
} from './types';

export const useFileUploadProgress = ({
  endPoint,
  additionalFormData,
  uploadFileService = uploadFile,
  initialFilesUploadCompletedInfo = {},
}: IUseFileUploadProgressOptions) => {
  const [filesUploadProgressInfo, setFilesUploadProgressInfo] = useState<FilesUploadProgressInfo>({});
  const [filesUploadCompletedInfo, setFilesUploadCompletedInfo] = useState<FilesUploadCompletedInfo>(initialFilesUploadCompletedInfo);
  const isUnMountedRef = useRef(false);

  const uploadFileWithProgressFromList = useCallback((fileList: IFileObject[]) => {
    // upload new files
    _.each(fileList, (fileObject) => {
      const { id, file, isValid } = fileObject;
      /* istanbul ignore else */
      if (isValid && !filesUploadProgressInfo[id] && !filesUploadCompletedInfo[id]) {
        // initial state
        setFilesUploadProgressInfo(prevState => ({
          ...prevState,
          [id]: {
            progress: 0,
          },
        }));
        // upload file
        uploadFileService({
          file,
          id,
          additionalFormData,
          endPoint,
          onUploadProgress: ({
            id: fileId, progress, total, completed, abort,
          }) => {
            // set progress
            setFilesUploadProgressInfo(prevState => ({
              ...prevState,
              [fileId]: {
                progress, total, completed, abort,
              },
            }));
          },
          onSuccess: ({
            id: fileId, response,
          }) => {
            // set complete success
            const { FileGuid } = response;
            setFilesUploadCompletedInfo(prevState => ({
              ...prevState,
              [fileId]: {
                success: !!FileGuid,
                FileGuid,
              },
            }));
          },
          onError: ({
            id: fileId, error,
          }) => {
            // set complete unsuccess
            setFilesUploadCompletedInfo(prevState => ({
              ...prevState,
              [fileId]: {
                success: false,
                error,
              },
            }));
            // reset progress
            setFilesUploadProgressInfo(prevState => ({
              ...prevState,
              [fileId]: {
                progress: -1,
                completed: true,
                error,
              },
            }));
          },
        });
      }
    });
    // clean-up and abort in-progress request if files are removed from fileList
    _.each(_.keys(filesUploadProgressInfo), (id) => {
      if (_.findIndex(fileList, { id }) === -1) {
        _.result(filesUploadProgressInfo[id], 'abort');
        delete filesUploadProgressInfo[id];
        setFilesUploadProgressInfo(filesUploadProgressInfo);
      }
    });
    // clean-up completed request if files are removed from fileList
    _.each(_.keys(filesUploadCompletedInfo), (id) => {
      if (_.findIndex(fileList, { id }) === -1) {
        // Suggestion: Call MT to also remove the file in Azure BLOB
        delete filesUploadCompletedInfo[id];
        setFilesUploadCompletedInfo(filesUploadCompletedInfo);
      }
    });
  }, [
    additionalFormData,
    endPoint,
    filesUploadCompletedInfo,
    filesUploadProgressInfo,
    uploadFileService,
  ]);

  // Ensure to abort each on-going request when unmount the component
  useEffect(() => () => {
    /* istanbul ignore next */
    isUnMountedRef.current = true;
  }, []);

  useEffect(() => () => {
    /* istanbul ignore if */
    if (isUnMountedRef.current) {
      _.each(filesUploadProgressInfo, (progressInfo) => {
        _.result(progressInfo, 'abort');
      });
    }
  }, [filesUploadProgressInfo]);

  return { filesUploadProgressInfo, filesUploadCompletedInfo, uploadFileWithProgressFromList };
};
